// src/components/AudioComponent.js
import GateRevealPanel from './GateRevealPanel';
import { getPanelRevealTitle } from '../utils/gatePresentation';
import React, {useMemo, useState} from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGateAccess } from '../hooks/useGateAccess';
import DigitalAssetPurchase from './DigitalAssetPurchase';
import ComponentControls from "./ComponentControls";
import ComponentAnalyticsSummary from './ComponentAnalyticsSummary';
import TimestampBadge from "./TimestampBadge";
import { resolveTypography } from '../utils/resolveTypography';
import GateBadge from "./GateBadge";
import DirectPaymentUnlock from "./DirectPaymentUnlock";
import PasswordUnlock from "./PasswordUnlock";
import { buildPanelClassName } from '../utils/panelTheme';

export default function AudioComponent({
                                           comp,
                                           index,
                                           moveUp,
                                           moveDown,
                                           onEdit,
                                           isEditor = false,
                                           removeComponent,

                                           // gating props
                                           gateType,
                                           selectedListId,
                                           onGateTypeChange,
                                           onGateListChange,
                                           extraGates,
                                           gateLogic,
                                           onExtraGatesChange,
                                           onGateLogicChange,
                                           donationCampaignOptions = [],
                                           donationGate,
                                           onDonationGateChange,
                                          onDirectPaymentGateChange,
                                          onDirectPaymentDraftChange,

                                           style = {},
                                           onStyleChange,
                                           coverUrl = '',
                                           onCoverChange,
                                           onPanelBackgroundImageChange,
                                           timestampDisplay = 'none',
                                           onTimestampDisplayChange,

                                           // NEW: global typography object from page (optional)
                                           pageTypography,
                                           ownerPublicKey = ''
                                       }) {
    const ownerPublicKeyStr = ownerPublicKey ? String(ownerPublicKey) : '';
    const normalizeGate = (g) => {
        if (!g || g.type !== "directPayment") return g;
        return { ...g, payToUid: ownerPublicKeyStr || g.payToUid || "" };
    };
    const gate  = normalizeGate(comp.gate || { type: 'public', listId: '' });
    const gates = useMemo(
        () => [
            gate,
            ...(Array.isArray(comp.extraGates) ? comp.extraGates.map(normalizeGate) : []),
        ],
        [gate, comp.extraGates, ownerPublicKeyStr]
    );
    const logic = comp.gateLogic === 'all' ? 'all' : 'any';

    const donationMap = useMemo(() => {
        const m = new Map();
        // expect options like { id, campaignName, currency }
        (donationCampaignOptions || []).forEach(o => {
            if (o?.id) {
                m.set(String(o.id), {
                    campaignName: o.campaignName || '',
                    currency: o.currency || 'SOL',
                    tokenMint: o.tokenMint || ''
                });
            }
        });
        return m;
    }, [donationCampaignOptions]);

    const [gateRefreshKey, setGateRefreshKey] = useState(0);
    const [hasAccess, isLoading, gateNamesLabel, , gateAccess] = useGateAccess(
        { gates, logic, donationLookup: donationMap },
        { spaceOwnerUid: ownerPublicKeyStr, isEditor: isEditor, refreshKey: gateRefreshKey, componentId: comp.id }
    );
    const { resetPasswordAccess, resetPasswordError } = gateAccess;
    const { username: routeUsername } = useParams();
    const safeUsername = routeUsername || comp?.muultiName || '';
    const donationGateLinks = useMemo(() => {
        if (!safeUsername) return [];
        return (gates || [])
            .filter(g => g?.type === 'donation' && g?.donationCompId)
            .map(g => ({
                id: String(g.donationCompId),
                name: g.campaignName || 'Donation',
                currency: g.currency || '',
                href: `/${safeUsername}/d/${g.donationCompId}`
            }));
    }, [gates, safeUsername]);

    // Resolve typography with per-panel overrides taking priority
    const typo = resolveTypography(pageTypography, style);

    const formatTs = (ts) => {
        if (!ts) return '';
        const d = (typeof ts === 'number') ? new Date(ts) : (ts.toDate ? ts.toDate() : new Date(ts));
        return d.toLocaleString();
    };

    const basePanelClass = buildPanelClassName({ style });
    const analyticsFooter = (
        <ComponentAnalyticsSummary
            comp={comp}
            ownerPublicKey={ownerPublicKeyStr}
            title="Component activity"
            compact
            footer
        />
    );

    if (isLoading) {
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
                <h3>Checking access for {gateNamesLabel}…</h3>
                <TimestampBadge
                    mode={comp.timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                {analyticsFooter}
            {!isLoading && hasAccess && <DigitalAssetPurchase comp={comp} ownerPublicKey={ownerPublicKeyStr} compact />}
                <ComponentControls
                    publishingComponent={comp}
                    publishingOwnerPublicKey={ownerPublicKeyStr}
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={onEdit}
                    removeComponent={removeComponent}
                    gateType={gate.type}
                    selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    timestampDisplay={comp.timestampDisplay || 'none'}
                    onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                />
            </div>
        </GateRevealPanel>
        );
    }

    if (!hasAccess) {
        const directPaymentGate =
            (gates || []).find(g => g?.type === "directPayment" && Number(g?.priceSol || 0) > 0);
        const passwordGate =
            (gates || []).find(g => g?.type === "password" && String(g?.password || '').length > 0);
        const deniedHeading = directPaymentGate ? 'x402 Error' : 'Access denied';
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={buildPanelClassName({ style, extra: "locked" })} style={style}>
                <h1>{deniedHeading} - {gateNamesLabel}</h1>
                {passwordGate ? (
                    <PasswordUnlock
                        gate={passwordGate}
                        componentId={comp.id}
                        onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                    />
                ) : null}
                {directPaymentGate ? (
                    <DirectPaymentUnlock
                        gate={directPaymentGate}
                        componentId={comp.id}
                        componentType={comp.type}
                        ownerPublicKey={ownerPublicKeyStr}
                        onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                    />
                ) : null}
                {donationGateLinks.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                        {donationGateLinks.map((d) => (
                            <div key={d.id} style={{ marginTop: 4 }}>
                                <Link to={d.href} className="detail-page-return-link">
                                    View {d.name}{d.currency ? ` (${d.currency})` : ''} details
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
                <TimestampBadge
                    mode={comp.timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                {analyticsFooter}
            {!isLoading && hasAccess && <DigitalAssetPurchase comp={comp} ownerPublicKey={ownerPublicKeyStr} compact />}
                <ComponentControls
                    publishingComponent={comp}
                    publishingOwnerPublicKey={ownerPublicKeyStr}
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={onEdit}
                    removeComponent={removeComponent}
                    gateType={gate.type}
                    selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    timestampDisplay={comp.timestampDisplay || 'none'}
                    onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                />
            </div>
        </GateRevealPanel>
        );
    }

    return (
        <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
            {comp.cover && (
                <img
                    src={comp.cover}
                    alt={comp.title || 'Audio cover'}
                    style={{ width: '100%', borderRadius: '8px', marginBottom: '8px' }}
                />
            )}

            {comp.title && (
                <h3
                    style={{
                        fontFamily: typo.headers?.fontFamily,
                        fontSize:   typo.headers?.fontSize,
                        fontWeight: typo.headers?.fontWeight,
                        color:      typo.headers?.color,
                        lineHeight: typo.headers?.lineHeight
                    }}
                >
                    {comp.title}
                </h3>
            )}

            {comp.description && (
                <p
                    style={{
                        fontFamily: typo.paragraph?.fontFamily,
                        fontSize:   typo.paragraph?.fontSize,
                        color:      typo.paragraph?.color,
                        lineHeight: typo.paragraph?.lineHeight,
                        whiteSpace: 'pre-wrap',
                        marginTop:  8
                    }}
                >
                    {comp.description}
                </p>
            )}

            {comp.url && (
                <audio
                    src={comp.url}
                    controls
                    style={{ width: '100%', marginTop: '8px' }}
                />
            )}


            {hasAccess && (
                <GateBadge gateNamesLabel={gateNamesLabel} onResetPasswordAccess={resetPasswordAccess} resetPasswordError={resetPasswordError} />
            )}
            <TimestampBadge
                mode={comp.timestampDisplay || 'none'}
                createdAt={comp.createdAt}
                updatedAt={comp.updatedAt}
            />
            {analyticsFooter}
            {!isLoading && hasAccess && <DigitalAssetPurchase comp={comp} ownerPublicKey={ownerPublicKeyStr} compact />}

            {/* only show edit controls in editor mode */}
            <ComponentControls
                    publishingComponent={comp}
                    publishingOwnerPublicKey={ownerPublicKeyStr}
                compId={comp.id}
                index={index}
                moveUp={moveUp}
                moveDown={moveDown}
                onEdit={onEdit}
                removeComponent={removeComponent}
                gateType={gate.type}
                selectedListId={gate.listId}
                onGateTypeChange={onGateTypeChange}
                onGateListChange={onGateListChange}
                extraGates={extraGates ?? comp.extraGates ?? []}
                gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                onExtraGatesChange={onExtraGatesChange}
                onGateLogicChange={onGateLogicChange}
                donationCampaignOptions={donationCampaignOptions}
                donationGate={donationGate ?? comp.gate}
                onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                style={style}
                onStyleChange={onStyleChange}
                coverUrl={coverUrl}
                onCoverChange={onCoverChange}
                onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                timestampDisplay={comp.timestampDisplay || 'none'}
                onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
            />
        </div>
        </GateRevealPanel>
    );
}



